import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBriefcase, 
  faUsers, 
  faGraduationCap, 
  faHandshake,
  faHeart,
  faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';
import { theme } from '../../../styles/theme';

// Import images from existing assets
import castleImage from '../../../assets/castle2.png';
import gardensImage from '../../../assets/gardens.png';

const CareersContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const IntroText = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.2rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  max-width: 800px;
  margin: 0 auto 3rem;
  text-align: center;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 4rem 0;
`;

const BenefitCard = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  text-align: center;
`;

const IconWrapper = styled.div`
  width: 70px;
  height: 70px;
  margin: 0 auto 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.colors.accent};
  border-radius: 50%;
  
  svg {
    color: white;
    font-size: 32px;
  }
`;

const BenefitTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const BenefitText = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.primary};
`;

const CurrentOpenings = styled.div`
  margin: 4rem 0;
`;

const OpeningCard = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin-bottom: 2rem;
`;

const OpeningTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.8rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const OpeningDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
`;

const ApplyButton = styled.button`
  display: inline-block;
  background-color: ${props => props.theme.colors.accent};
  color: white;
  padding: 1rem 2rem;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  cursor: pointer;
  
  &:hover {
    background-color: transparent;
    color: ${props => props.theme.colors.accent};
    border-color: ${props => props.theme.colors.accent};
  }
`;

const ContactSection = styled.div`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  padding: 4rem 2rem;
  text-align: center;
  margin-top: 4rem;
`;

const ContactTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  margin-bottom: 1.5rem;
`;

const ContactText = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  max-width: 800px;
  margin: 0 auto 2rem;
`;

const Careers = () => {
  return (
    <CareersContainer>
      <ParallaxBanner 
        title="Careers at Belvoir Castle"
        subtitle="Join Our Historic Team"
        image={castleImage}
      />
      
      <ContentContainer>
        <SectionTitle>Build Your Career in a Historic Setting</SectionTitle>
        <IntroText>
          At Belvoir Castle, we're not just preserving history - we're creating it every day. 
          Join our dedicated team and become part of a unique heritage that spans centuries. 
          Whether you're passionate about hospitality, conservation, or customer service, 
          we offer rewarding opportunities in a truly special environment.
        </IntroText>
        
        <BenefitsGrid>
          <BenefitCard>
            <IconWrapper>
              <FontAwesomeIcon icon={faHeart} />
            </IconWrapper>
            <BenefitTitle>Passionate Team</BenefitTitle>
            <BenefitText>
              Work alongside dedicated professionals who share your passion for history, 
              hospitality, and excellence.
            </BenefitText>
          </BenefitCard>
          
          <BenefitCard>
            <IconWrapper>
              <FontAwesomeIcon icon={faGraduationCap} />
            </IconWrapper>
            <BenefitTitle>Growth Opportunities</BenefitTitle>
            <BenefitText>
              We invest in our team's development through training programs and 
              career advancement opportunities.
            </BenefitText>
          </BenefitCard>
          
          <BenefitCard>
            <IconWrapper>
              <FontAwesomeIcon icon={faHandshake} />
            </IconWrapper>
            <BenefitTitle>Work-Life Balance</BenefitTitle>
            <BenefitText>
              Enjoy flexible working arrangements and a supportive environment that 
              values your well-being.
            </BenefitText>
          </BenefitCard>
        </BenefitsGrid>
        
        <CurrentOpenings>
          <SectionTitle>Current Opportunities</SectionTitle>
          
          <OpeningCard>
            <OpeningTitle>No Positions Currently Available</OpeningTitle>
            <OpeningDescription>
              While we don't have any specific positions open at the moment, we're always interested 
              in hearing from talented individuals who are passionate about heritage and hospitality. 
              Feel free to send your CV for future opportunities.
            </OpeningDescription>
          </OpeningCard>
        </CurrentOpenings>
        
        <ContactSection>
          <ContactTitle>Join Our Team</ContactTitle>
          <ContactText>
            If you don't see a role that matches your skills but believe you could contribute 
            to our team, we'd love to hear from you. Send your CV and a cover letter to our 
            HR department.
          </ContactText>
          <ApplyButton 
            onClick={() => {
              try {
                window.open('mailto:info@belvoircastle.com', '_blank');
              } catch (e) {
                alert('Please email us at info@belvoircastle.com');
              }
            }}
          >
            Contact Us
          </ApplyButton>
        </ContactSection>
      </ContentContainer>
      
      <Footer />
    </CareersContainer>
  );
};

export default Careers; 